"use client";

import { useCallback, useMemo, useState } from "react";
import { useTranslations } from "next-intl";

import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { HttpError } from "@utilities/api";
import { logger } from "@utilities/logger";
import {
  InteractiveImage,
  type InteractiveImageGenerate,
  type InteractiveImageGenerationError,
  type InteractiveImageProps,
} from "@ui/components/demos/InteractiveImage";

import { buildClaimUrl, toCtaUrl } from "../CreativeTemplate/ctaUrls";
import { buildOnGenerate } from "./buildOnGenerate";
import { buildImageVideoRedirectUrl } from "./ctaUrls";
import { mapGenerationError } from "./mapGenerationError";

/** Cap the logged prompt so unbounded free-text input can't bloat PostHog properties. */
const PROMPT_LOG_MAX_LENGTH = 500;

interface InteractiveImageLiveProps extends Omit<
  InteractiveImageProps,
  "onGenerate" | "generationError" | "onGenerationErrorClear" | "creationResults"
> {
  /** Legacy template slug for a first generation (text → image). */
  creationTemplateSlug: string;
  /** Legacy template slug for an edit (image + text → image). */
  editionTemplateSlug: string;
}

/**
 * Live-generation wrapper around the pure {@link InteractiveImage}. Owns the network wiring and
 * error state so `_ui` stays backend-agnostic; quota/rate-limit failures surface the sign-up CTA,
 * any other failure an inline retry.
 */
export function InteractiveImageLive({
  creationTemplateSlug,
  editionTemplateSlug,
  ...rest
}: InteractiveImageLiveProps) {
  const t = useTranslations("ClientKeys.InteractiveImage");
  const [generationError, setGenerationError] = useState<InteractiveImageGenerationError | null>(null);

  const generate = useMemo(
    () => buildOnGenerate({ creationTemplateSlug, editionTemplateSlug }),
    [creationTemplateSlug, editionTemplateSlug],
  );

  const onGenerate = useCallback<InteractiveImageGenerate>(
    async input => {
      setGenerationError(null);
      const trimmedPrompt = input.prompt.trim();
      // Same anonymous creative-templates flow as the Hero Omnibox, so it reuses the omnibox events;
      // `surface` lets omnibox dashboards filter this demo out (`where surface = omnibox`).
      const prompt = trimmedPrompt.slice(0, PROMPT_LOG_MAX_LENGTH);
      const eventProps = {
        surface: "interactive_image",
        input_type: input.attachments.length > 0 ? "file" : "text",
        prompt,
      };

      captureEvent(EventName.mw_creative_template_omnibox_generation_started, eventProps);

      // Resolved by buildOnGenerate (chained base id or freshly uploaded reference); lets the gated
      // CTA deep-link the input asset even when generation fails after the upload.
      let sourceAssetId = input.baseImageAssetId;

      try {
        const outcome = await generate({ ...input, onSourceAssetResolved: id => (sourceAssetId = id) });
        captureEvent(EventName.mw_creative_template_omnibox_generation_succeeded, eventProps);
        return {
          imageUrl: outcome.imageUrl,
          alt: outcome.alt,
          assetId: outcome.assetId,
          // Deep-link to image-video to claim the generated asset (sign-up-wrapped for anonymous visitors).
          claimUrl: toCtaUrl(
            buildClaimUrl({
              assetId: outcome.assetId,
              modelId: outcome.modelId,
              refinedPrompt: outcome.refinedPrompt ?? (trimmedPrompt || undefined),
            }),
          ),
        };
      } catch (err) {
        // Aborted = silent cancel; let the component's request-id guard drop it (no terminal event).
        if (input.signal.aborted) throw err;
        const mapped = mapGenerationError(err, { gated: t("error_quota"), retry: t("error_generic") });
        const errorType = err instanceof HttpError ? err.errorType : null;
        if (mapped.kind === "gated") {
          captureEvent(EventName.mw_creative_template_omnibox_quota_exceeded, { ...eventProps, error_type: errorType });
          // Prefer the API's refined prompt when it provides one (as the Hero Omnibox does), so
          // image-video opens with what was actually generated against; else the raw user prompt.
          const refinedPrompt = err instanceof HttpError ? (err.data?.refined_prompt as string | undefined) : undefined;
          // Limit reached: send them to image-video prefilled with the prompt and input asset (if any).
          setGenerationError({
            ...mapped,
            ctaUrl: toCtaUrl(
              buildImageVideoRedirectUrl({
                prompt: refinedPrompt || trimmedPrompt || undefined,
                assetId: sourceAssetId,
              }),
            ),
          });
        } else {
          captureEvent(EventName.mw_creative_template_omnibox_generation_failed, {
            ...eventProps,
            error_type: errorType,
          });
          // Only log non-gated failures; gated ones are normal quota flow.
          logger.error(err instanceof Error ? err : null, "[InteractiveImageLive] generation failed");
          setGenerationError(mapped);
        }
        throw err;
      }
    },
    [generate, t],
  );

  return (
    <InteractiveImage
      {...rest}
      onGenerate={onGenerate}
      generationError={generationError ?? undefined}
      onGenerationErrorClear={() => setGenerationError(null)}
    />
  );
}
